import React, { useEffect } from 'react';
import { connect } from 'react-redux';

import PageSection from '@RHEnvironmental/pattern-library/dist/js/components/PageSection';
import LoadingIndicator from '@RHEnvironmental/pattern-library/dist/js/components/LoadingIndicator';
import PageHeading from '@RHEnvironmental/pattern-library/dist/js/components/PageHeading';
import Table from '@RHEnvironmental/pattern-library/dist/js/components/Table';
import OrderedList from '@RHEnvironmental/pattern-library/dist/js/components/OrderedList';
import ListItem from '@RHEnvironmental/pattern-library/dist/js/components/ListItem';
import UnorderedList from '@RHEnvironmental/pattern-library/dist/js/components/UnorderedList';
import * as inspectionDetailActions from '../../../actions/inspectionDetail';
import { boolToYesNo, pluralise } from '../../../services/ui/lang';

import dateFormat from '../../../services/ui/date';
import BodyText from '../../modules/BodyText';
import {
    getManagers,
    getOccupants,
    getOwners,
    getResidentLandlords,
    getTenants,
} from '../../../services/contacts';
import Thumbnail from '../../modules/Thumbnail';
import { filterScoredHazards } from '../../../selectors/inspectionExport';
import { getPhotosFromDefect } from '../../../services/documents';
import { getCreatorName } from '../../../services/text/getCreatorName';

const DefectTable = ({ defect, hazard, isNewDesign }) => (
    <Table className="table--full-width mb-4">
        <thead>
            <tr>
                <th>{defect.reference}</th>
                <th />
            </tr>
        </thead>
        <tbody>
            {!!defect.elements?.length && (
                <tr>
                    <td>Elements</td>
                    <td>
                        {defect.elements
                            .map((element) => element.name)
                            .join(', ')}
                    </td>
                </tr>
            )}
            <tr>
                <td>Date Added</td>
                <td>{dateFormat.standard(defect.created_at)}</td>
            </tr>
            {defect.hazards && hazard && (
                <tr>
                    <td>Other Hazards</td>
                    <td>
                        {defect.hazards
                            .filter((aHazard) => aHazard.id !== hazard.id)
                            .map((aHazard) => aHazard.name)
                            .join(', ')}
                    </td>
                </tr>
            )}
            {defect.tags.length > 0 && !isNewDesign && (
                <tr>
                    <td>Tags</td>
                    <td>{defect.tags.map((tag) => tag.name).join(', ')}</td>
                </tr>
            )}
            <tr>
                <td>Location</td>
                <td>
                    {defect.location.display_name} (
                    {defect.location.reference})
                </td>
            </tr>
            <tr>
                <td>Action</td>
                <td>{defect.action_type?.name}</td>
            </tr>
            <tr>
                <td>Photos</td>
                <td>
                    {defect.photos.length > 0 ? (
                        <div className="photo-grid">
                            {defect.photos.map(
                                (photo) =>
                                    photo.include_in_report && (
                                        <Thumbnail
                                            className="photo-grid__thumbnail"
                                            photoId={photo.id}
                                        />
                                    )
                            )}
                        </div>
                    ) : (
                        <span>No photos added</span>
                    )}
                </td>
            </tr>

            {defect.notes && (
                <tr>
                    <td>Notes</td>
                    <td>
                        {defect.notes.map((note) => (
                            <div className="summary__note summary__note--blue">
                                <BodyText className="summary__note-header">
                                    {getCreatorName(note)} &bull;{' '}
                                    {dateFormat.standard(note.client_updated_at ?? note.updated_at)}
                                </BodyText>
                                <BodyText className="summary__note-content">
                                    {' '}
                                    &bull; {note.content}
                                </BodyText>
                            </div>
                        ))}
                    </td>
                </tr>
            )}
        </tbody>
    </Table>
);

const InspectionExport = ({
    isNewDesign,
    match: {
        params: { id, token },
    },
    inspection,
    creator,
    scoring,
    getInspectionByToken,
    reset,
}) => {
    useEffect(() => {
        getInspectionByToken(token);

        return () => {
            reset();
        };
    }, [getInspectionByToken, id, reset, token]);

    const residentLandlords = getResidentLandlords(inspection?.contacts);
    const tenants = getTenants(inspection?.contacts);
    const occupants = getOccupants(inspection?.contacts);
    const owners = getOwners(inspection?.contacts);
    const managers = getManagers(inspection?.contacts);

    return (
        <div className="page-section--pdf">
            {!inspection ? (
                <PageSection className="page-section--fixed-width">
                    <LoadingIndicator show className="mt-4" />
                </PageSection>
            ) : (
                <table>
                    <thead>
                        <tr>
                            <td>
                                <BodyText>
                                    <strong>Ref:</strong> {inspection.reference}{' '}
                                    / <strong>Address:</strong>{' '}
                                    {inspection._formatted.address ??
                                        'Not known'}{' '}
                                    / <strong>UPRN:</strong>{' '}
                                    {inspection._formatted.uprn ?? 'Not known'}
                                    <br />/ <strong>
                                        Inspection Date:
                                    </strong>{' '}
                                    {inspection._formatted.inspection_date} /{' '}
                                    <strong>Status:</strong>{' '}
                                    {inspection.status.name}
                                </BodyText>
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <PageHeading level={1} className="page-title">
                                    Property <br />
                                    Inspection <br />
                                    Report
                                </PageHeading>
                                <Table className="table--full-width">
                                    <thead>
                                        <tr>
                                            <th />
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <PageHeading level={4}>
                                                    Reference
                                                </PageHeading>
                                            </td>
                                            <td>{inspection.reference}</td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <PageHeading level={4}>
                                                    Prepared By
                                                </PageHeading>
                                            </td>
                                            <td>
                                                {creator.name} (
                                                {creator.provider}) on{' '}
                                                {dateFormat.now()}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <PageHeading level={4}>
                                                    Inspection Date
                                                </PageHeading>
                                            </td>
                                            <td>
                                                {
                                                    inspection._formatted
                                                        .inspection_date
                                                }
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <PageHeading level={4}>
                                                    Property Address
                                                </PageHeading>
                                            </td>
                                            <td>
                                                {inspection.property?.address
                                                    ?.singleline ?? 'Not known'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <PageHeading level={4}>
                                                    Property UPRN
                                                </PageHeading>
                                            </td>
                                            <td>
                                                {inspection._formatted.uprn}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <PageHeading level={4}>
                                                    Status
                                                </PageHeading>
                                            </td>
                                            <td>{inspection.status.name}</td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <PageHeading level={4}>
                                                    Action Required
                                                </PageHeading>
                                            </td>
                                            <td>
                                                {
                                                    inspection._formatted
                                                        .action_required
                                                }
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <PageHeading level={4}>
                                                    {isNewDesign ? 'Inspector' : 'Officer'}
                                                </PageHeading>
                                            </td>
                                            <td>
                                                {getCreatorName(inspection)}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <PageHeading level={4}>
                                                    Weather
                                                </PageHeading>
                                            </td>
                                            <td>
                                                {inspection.weather_conditions ??
                                                    'Not known'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>

                                <div className="page-section--break" />

                                <PageHeading level={1} className="blue mb-4">
                                    Table of Contents
                                </PageHeading>

                                <div className="table-of-contents">
                                    <OrderedList>
                                        <ListItem>
                                            Property Details
                                            <OrderedList>
                                                <ListItem>Property</ListItem>
                                                <ListItem>Dwelling</ListItem>
                                            </OrderedList>
                                        </ListItem>
                                        <ListItem>
                                            Occupancy Details
                                            <OrderedList>
                                                <ListItem>Occupancy</ListItem>
                                                <ListItem>Tenancy</ListItem>
                                                <ListItem>Occupier</ListItem>
                                            </OrderedList>
                                        </ListItem>
                                        <ListItem>
                                            Ownership and Management Details
                                            <OrderedList>
                                                <ListItem>Owners</ListItem>
                                                <ListItem>Managers</ListItem>
                                            </OrderedList>
                                        </ListItem>
                                        <ListItem>
                                            HHSRS Scoring Summary
                                        </ListItem>
                                        <ListItem>
                                            {`Hazards and ${isNewDesign ? 'Findings' : 'Defects'} Summary`}
                                            {inspection._formatted
                                                .hazard_defects &&
                                                inspection._formatted
                                                    .hazard_defects.length >
                                                    0 && (
                                                    <OrderedList>
                                                        {inspection._formatted.hazard_defects.map(
                                                            (hazard) => (
                                                                <ListItem>
                                                                    {
                                                                        hazard.name
                                                                    }
                                                                </ListItem>
                                                            )
                                                        )}
                                                    </OrderedList>
                                                )}
                                        </ListItem>
                                        <ListItem>
                                            Other findings summary
                                        </ListItem>
                                        <ListItem>
                                            Actions
                                            <OrderedList>
                                                {Object.keys(
                                                    inspection._formatted
                                                        .actions
                                                ).map((action) => (
                                                    <ListItem>
                                                        {action ? action : 'None added'}
                                                    </ListItem>
                                                ))}
                                            </OrderedList>
                                        </ListItem>
                                        <ListItem>
                                            Annexes
                                            <OrderedList>
                                                <ListItem>Gallery</ListItem>
                                            </OrderedList>
                                        </ListItem>
                                    </OrderedList>
                                </div>

                                <div className="page-section--break" />

                                <PageHeading level={1} className="blue mb-4">
                                    1.0 Property Details
                                </PageHeading>

                                <PageHeading level={3} className="blue mb-4">
                                    1.1 Property
                                </PageHeading>

                                <Table className="table--full-width">
                                    <thead>
                                        <tr>
                                            <th />
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>1.1.1 Address</td>
                                            <td>
                                                {inspection.property.address ? (
                                                    <>
                                                        {
                                                            inspection
                                                                ._formatted
                                                                .address
                                                        }
                                                    </>
                                                ) : (
                                                    <BodyText>
                                                        Not known
                                                    </BodyText>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>1.1.2 UPRN</td>
                                            <td>
                                                {inspection.property?.address
                                                    ?.uprn ?? 'Not known'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>1.1.3 Dwelling Type</td>
                                            <td>
                                                {inspection.property?.is_hmo !=
                                                    null && (
                                                    <span>
                                                        {inspection.property
                                                            .is_hmo
                                                            ? 'HMO'
                                                            : 'Non-HMO'}{' '}
                                                    </span>
                                                )}

                                                {inspection.property?.type && (
                                                    <span>
                                                        {
                                                            inspection.property
                                                                .type.name
                                                        }
                                                    </span>
                                                )}

                                                {!inspection.property?.type &&
                                                    inspection.property
                                                        .is_hmo === null && (
                                                        <span>Not known</span>
                                                    )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>1.1.4 Dwelling Age</td>
                                            <td>
                                                {inspection.property.age
                                                    ? inspection.property.age
                                                          .name
                                                    : 'Not known'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>1.1.5 Property Detachment</td>
                                            <td>
                                                {inspection.property.detachment
                                                    ? inspection.property
                                                          .detachment.name
                                                    : 'Not known'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>1.1.6 Property Description</td>
                                            <td>
                                                {inspection.property.description
                                                    ? inspection.property
                                                          .description
                                                    : 'Not yet completed'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                1.1.7 Is the dwelling Council or
                                                Registered Social Landlord (RSL)
                                                owned?
                                            </td>
                                            <td>
                                                {boolToYesNo(
                                                    inspection.property
                                                        .council_or_rsl_owned
                                                ) ?? 'Not known'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>1.1.8 Radon level</td>
                                            <td>
                                                {inspection.property.radon
                                                    ? inspection.property.radon
                                                    : 'Not known'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>

                                <PageHeading
                                    level={3}
                                    className="blue mb-4 mt-4"
                                >
                                    1.2 Dwelling
                                </PageHeading>

                                <Table className="table--full-width">
                                    <thead>
                                        <tr>
                                            <th />
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>1.2.1 Floors in Building</td>
                                            <td>
                                                {inspection.property.floors ??
                                                    'Not known'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                1.2.2 Total Dwellings in
                                                Building
                                            </td>
                                            <td>
                                                {inspection.property
                                                    .dwellings ?? 'Not known'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>1.2.3 Mixed Commercial Use</td>
                                            <td>
                                                {' '}
                                                {boolToYesNo(
                                                    inspection.property
                                                        .mixed_commercial
                                                ) ?? 'Not known'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>1.2.4 EPC</td>
                                            <td>
                                                {inspection.property.epc ? (
                                                    <UnorderedList>
                                                        {inspection.property.epc
                                                            .current_energy_rating ? (
                                                            <ListItem>
                                                                Current Energy
                                                                Rating: Band{' '}
                                                                {
                                                                    inspection
                                                                        .epc_bands
                                                                        .current_energy_band
                                                                }{' '}
                                                                -{' '}
                                                                {
                                                                    inspection
                                                                        .property
                                                                        .epc
                                                                        .current_energy_rating
                                                                }
                                                            </ListItem>
                                                        ) : (
                                                            <ListItem>
                                                                Current Energy
                                                                Rating: Not
                                                                known
                                                            </ListItem>
                                                        )}
                                                        {inspection.property.epc
                                                            .potential_energy_rating ? (
                                                            <ListItem>
                                                                Potential Energy
                                                                Rating: Band{' '}
                                                                {
                                                                    inspection
                                                                        .epc_bands
                                                                        .potential_energy_band
                                                                }{' '}
                                                                -{' '}
                                                                {
                                                                    inspection
                                                                        .property
                                                                        .epc
                                                                        .potential_energy_rating
                                                                }
                                                            </ListItem>
                                                        ) : (
                                                            <ListItem>
                                                                Potential Energy
                                                                Rating: Not
                                                                known
                                                            </ListItem>
                                                        )}
                                                        {inspection.property.epc
                                                            .environment_impact_current ? (
                                                            <ListItem>
                                                                Current
                                                                Environment
                                                                Impact: Band{' '}
                                                                {
                                                                    inspection
                                                                        .epc_bands
                                                                        .environment_impact_current_band
                                                                }{' '}
                                                                -{' '}
                                                                {
                                                                    inspection
                                                                        .property
                                                                        .epc
                                                                        .environment_impact_current
                                                                }
                                                            </ListItem>
                                                        ) : (
                                                            <ListItem>
                                                                Current
                                                                Environment
                                                                Impact: Not
                                                                known
                                                            </ListItem>
                                                        )}
                                                        {inspection.property.epc
                                                            .environment_impact_potential ? (
                                                            <ListItem>
                                                                Potential
                                                                Environment
                                                                Impact: Band{' '}
                                                                {
                                                                    inspection
                                                                        .epc_bands
                                                                        .environment_impact_potential_band
                                                                }{' '}
                                                                -{' '}
                                                                {
                                                                    inspection
                                                                        .property
                                                                        .epc
                                                                        .environment_impact_potential
                                                                }
                                                            </ListItem>
                                                        ) : (
                                                            <ListItem>
                                                                Potential
                                                                Environment
                                                                Impact: Not
                                                                known
                                                            </ListItem>
                                                        )}
                                                    </UnorderedList>
                                                ) : (
                                                    <>Not known</>
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>

                                <div className="page-section--break" />

                                <PageHeading level={1} className="blue mb-4">
                                    2.0 Occupancy Details
                                </PageHeading>

                                <PageHeading level={3} className="blue mb-4">
                                    2.1 Occupancy
                                </PageHeading>

                                <Table className="table--full-width mb-4">
                                    <thead>
                                        <tr>
                                            <th />
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>2.1.1 Number of Occupiers</td>
                                            <td>{occupants.length ?? 0}</td>
                                        </tr>
                                        <tr>
                                            <td>2.1.2 Number of Households</td>
                                            <td>
                                                {inspection.households.length >
                                                0
                                                    ? inspection.households
                                                          .length
                                                    : 'Not known'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>2.1.3 Resident Landlords</td>
                                            <td>
                                                {residentLandlords &&
                                                residentLandlords.length > 0 ? (
                                                    residentLandlords.map(
                                                        (contact) => (
                                                            <div>
                                                                <BodyText>
                                                                    {
                                                                        contact.full_name
                                                                    }
                                                                </BodyText>
                                                            </div>
                                                        )
                                                    )
                                                ) : (
                                                    <>Not known</>
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>

                                <PageHeading level={3} className="blue mb-4">
                                    2.2 Tenancy
                                </PageHeading>

                                {tenants && tenants.length > 0 ? (
                                    <Table className="table--full-width mb-4">
                                        <thead>
                                            <tr>
                                                <th>Contact</th>
                                                <th>Tenancy Type</th>
                                                <th>Occupancy Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tenants.map((contact) => (
                                                <tr>
                                                    <td>
                                                        Name:{' '}
                                                        {contact.full_name ??
                                                            'Not known'}
                                                        <br />
                                                        Phone:{' '}
                                                        {contact.phone ??
                                                            'Not known'}
                                                        <br />
                                                        Email:{' '}
                                                        {contact.email ??
                                                            'Not known'}
                                                    </td>
                                                    <td>
                                                        {contact.occupant.tenant
                                                            .type?.name ??
                                                            'Not known'}
                                                    </td>
                                                    <td>
                                                        {contact.occupant
                                                            ._formatted
                                                            .occupancy_date ??
                                                            'Not known'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <>Not known</>
                                )}

                                <PageHeading level={3} className="blue mb-4">
                                    2.3 Occupier
                                </PageHeading>

                                {occupants && occupants.length > 0 ? (
                                    <Table className="table--full-width mb-4">
                                        <thead>
                                            <tr>
                                                <th>Contact</th>
                                                <th>Household</th>
                                                <th>Gender</th>
                                                <th>Age Range</th>
                                                <th>Relation</th>
                                                <th>Occupancy Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {occupants.map((contact) => (
                                                <tr>
                                                    <td>
                                                        Name:{' '}
                                                        {contact.full_name ??
                                                            'Not known'}
                                                        <br />
                                                        Phone:{' '}
                                                        {contact.phone ??
                                                            'Not known'}
                                                        <br />
                                                        Email:{' '}
                                                        {contact.email ??
                                                            'Not known'}
                                                    </td>
                                                    <td>
                                                        {contact.occupant
                                                            .household
                                                            ?.reference ??
                                                            'Not known'}
                                                    </td>
                                                    <td>
                                                        {contact.occupant
                                                            .gender_name ??
                                                            'Not known'}
                                                    </td>
                                                    <td>
                                                        {contact.occupant
                                                            .age_range?.name ??
                                                            'Not known'}
                                                    </td>
                                                    <td>
                                                        {
                                                            contact._formatted
                                                                .type_singleLine
                                                        }
                                                    </td>
                                                    <td>
                                                        {contact.occupant
                                                            ._formatted
                                                            .occupancy_date ??
                                                            'Not known'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <>Not known</>
                                )}

                                <div className="page-section--break" />

                                <PageHeading level={1} className="blue mb-4">
                                    3.0 Ownership and Management Details
                                </PageHeading>

                                <PageHeading level={3} className="blue mb-4">
                                    3.1 Owners
                                </PageHeading>

                                {owners && owners.length > 0 ? (
                                    <Table className="table--full-width mb-4">
                                        <thead>
                                            <tr>
                                                <th>Contact</th>
                                                <th>Address</th>
                                                <th>Organisation</th>
                                                <th>Owner Type</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {owners.map((contact) => (
                                                <tr>
                                                    <td>
                                                        Name:{' '}
                                                        {contact.full_name ??
                                                            'Not known'}
                                                        <br />
                                                        Phone:{' '}
                                                        {contact.phone ??
                                                            'Not known'}
                                                        <br />
                                                        Email:{' '}
                                                        {contact.email ??
                                                            'Not known'}
                                                        <br />
                                                    </td>
                                                    <td>
                                                        {contact.address
                                                            ?.singleline ??
                                                            'Not known'}
                                                    </td>
                                                    <td>
                                                        {contact.organisation_name ??
                                                            'Not known'}
                                                    </td>
                                                    <td>
                                                        {contact.type?.name ??
                                                            'Not known'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <BodyText>Not known</BodyText>
                                )}

                                <PageHeading level={3} className="blue mb-4">
                                    3.2 Managers
                                </PageHeading>

                                {managers && managers.length > 0 ? (
                                    <Table className="table--full-width mb-4">
                                        <thead>
                                            <tr>
                                                <th>Contact</th>
                                                <th>Address</th>
                                                <th>Organisation</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {managers.map((contact) => (
                                                <tr>
                                                    <td>
                                                        Name:{' '}
                                                        {contact.full_name ??
                                                            'Not known'}
                                                        <br />
                                                        Phone:{' '}
                                                        {contact.phone ??
                                                            'Not known'}
                                                        <br />
                                                        Email:{' '}
                                                        {contact.email ??
                                                            'Not known'}
                                                        <br />
                                                    </td>
                                                    <td>
                                                        {contact.address
                                                            ?.singleline ??
                                                            'Not known'}
                                                    </td>
                                                    <td>
                                                        {contact.organisation_name ??
                                                            'Not known'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <BodyText>Not known</BodyText>
                                )}

                                <div className="page-section--break" />

                                <PageHeading level={1} className="blue mb-4">
                                    4.0 HHSRS Scoring Summary
                                </PageHeading>

                                {scoring?.length > 0 ? (
                                    <>
                                        {scoring.map((hazard) => (
                                            <Table className="table--full-width mb-4">
                                                <thead>
                                                    <tr>
                                                        <th>{hazard.name}</th>
                                                        <th />
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>Category</td>
                                                        <td>
                                                            {hazard.scoring
                                                                ?.score
                                                                ? `Cat ${hazard.scoring.category}`
                                                                : ''}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>Score</td>
                                                        <td>
                                                            {hazard.scoring
                                                                ?.score
                                                                ? Number(
                                                                      hazard
                                                                          .scoring
                                                                          .score
                                                                  ).toFixed(1)
                                                                : ''}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>Rating</td>
                                                        <td>
                                                            {hazard.scoring
                                                                ? `Band ${hazard.scoring.band}`
                                                                : 'Incomplete'}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            Likelihood
                                                            Justification
                                                        </td>
                                                        <td>
                                                            {
                                                                hazard.scoring
                                                                    ?.likelihood_justification
                                                            }
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            Spread of Harms
                                                            Justification
                                                        </td>
                                                        <td>
                                                            {
                                                                hazard.scoring
                                                                    ?.harms_justification
                                                            }
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        ))}
                                    </>
                                ) : (
                                    <BodyText>Not yet scored</BodyText>
                                )}

                                <div className="page-section--break" />

                                <PageHeading level={1} className="blue mb-4">
                                    {`5.0 Hazards and ${isNewDesign ? 'Findings' : 'Defects'} Summary`}
                                </PageHeading>

                                {inspection._formatted.hazard_defects &&
                                inspection._formatted.hazard_defects.length >
                                    0 ? (
                                    <>
                                        {inspection._formatted.hazard_defects.map(
                                            (hazard, index) => (
                                                <>
                                                    <PageHeading
                                                        level={3}
                                                        className="blue mb-4"
                                                    >
                                                        5.{index + 1}{' '}
                                                        {hazard.name}
                                                    </PageHeading>

                                                    <BodyText>
                                                        {hazard.defects.length}{' '}
                                                        <strong>
                                                            {pluralise(
                                                                hazard.defects
                                                                    .length,
                                                                isNewDesign ? 'finding' : 'defect'
                                                            )}
                                                        </strong>{' '}
                                                        reported
                                                    </BodyText>

                                                    {hazard.defects.map(
                                                        (defect) => (
                                                            <DefectTable
                                                                hazard={hazard}
                                                                defect={defect}
                                                                isNewDesign={isNewDesign}
                                                            />
                                                        )
                                                    )}
                                                    <div className="page-section--break" />
                                                </>
                                            )
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <BodyText>Not known</BodyText>
                                        <div className="page-section--break" />
                                    </>
                                )}

                                <PageHeading level={1} className="blue mb-4">
                                    6.0 Other Findings Summary
                                </PageHeading>

                                {inspection.other_findings &&
                                inspection.other_findings.length > 0 ? (
                                    <>
                                        {inspection.other_findings.map(
                                            (defect) => (
                                                <>
                                                    <DefectTable
                                                        defect={defect}
                                                        isNewDesign={isNewDesign}
                                                    />
                                                    <div className="page-section--break" />
                                                </>
                                            )
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <BodyText>Not known</BodyText>
                                        <div className="page-section--break" />
                                    </>
                                )}

                                <PageHeading level={1} className="blue mb-4">
                                    7.0 Actions
                                </PageHeading>

                                {inspection._formatted.actions &&
                                Object.keys(inspection._formatted.actions)
                                    .length > 0 ? (
                                    <>
                                        {Object.keys(
                                            inspection._formatted.actions
                                        ).map((action, index) => (
                                            <>
                                                <PageHeading
                                                    level={3}
                                                    className="blue mb-4"
                                                >
                                                    7.{index + 1} {action ? action : 'None added'}
                                                </PageHeading>
                                                <Table className="table--full-width mb-4">
                                                    <thead>
                                                        <tr>
                                                            <th>Reference</th>
                                                            <th>Date Added</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {inspection._formatted.actions[
                                                            action
                                                        ].map((defect) => (
                                                            <tr>
                                                                <td>
                                                                    {
                                                                        defect.reference
                                                                    }
                                                                </td>
                                                                <td>
                                                                    {dateFormat.standard(
                                                                        defect.created_at
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </>
                                        ))}
                                    </>
                                ) : (
                                    <>
                                        <BodyText>Not known</BodyText>
                                        <div className="page-section--break" />
                                    </>
                                )}
                                <div className="page-section--break" />

                                <PageHeading level={1} className="blue mb-4">
                                    8.0 Annex
                                </PageHeading>

                                <PageHeading level={3} className="blue mb-4">
                                    8.1 Gallery
                                </PageHeading>

                                {inspection.defects.length > 0 ? (
                                    <div className="gallery">
                                        {inspection.defects.map((defect) =>
                                            defect.photos.length >
                                            0 ? (
                                                <>
                                                    {defect.photos
                                                        .filter(
                                                            (photo) =>
                                                                photo.include_in_report
                                                        )
                                                        .map((photo) => (
                                                            <Table className="table--full-width mb-4">
                                                                <thead>
                                                                    <tr>
                                                                        <th />
                                                                        <th />
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr>
                                                                        <td>
                                                                            Reference
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                defect.reference
                                                                            }
                                                                            {defect.hazards &&
                                                                            defect
                                                                                .hazards
                                                                                .length >
                                                                                0 ? (
                                                                                <span className="finding__capsule--defect">
                                                                                    {isNewDesign ? 'Finding' : 'Defect'}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="finding__capsule">
                                                                                    Other
                                                                                    finding
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td>
                                                                            Photo
                                                                            Added
                                                                        </td>
                                                                        <td>
                                                                            {dateFormat.standard(
                                                                                photo.created_at
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td>
                                                                            Photo
                                                                            Label
                                                                        </td>
                                                                        <td>
                                                                            {photo._formatted &&
                                                                                photo
                                                                                    ._formatted
                                                                                    .display_name}
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                                <tfoot>
                                                                    <tr>
                                                                        <td>
                                                                            <Thumbnail
                                                                                className="gallery__thumbnail"
                                                                                photoId={
                                                                                    photo.id
                                                                                }
                                                                            />
                                                                        </td>
                                                                    </tr>
                                                                </tfoot>
                                                            </Table>
                                                        ))}
                                                </>
                                            ) : (
                                                <Table className="table--full-width mb-4">
                                                    <thead>
                                                        <tr>
                                                            <th />
                                                            <th />
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>Reference</td>
                                                            <td>
                                                                {
                                                                    defect.reference
                                                                }
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                    <tfoot>
                                                        <tr>
                                                            <td>
                                                                No photos added
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                </Table>
                                            )
                                        )}
                                    </div>
                                ) : (
                                    <BodyText>No photos available</BodyText>
                                )}
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>
                                <div className="report-footer">
                                    <strong>Prepared By: </strong>
                                    {creator.name} ({creator.provider}) on{' '}
                                    {dateFormat.now()}
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    inspection: state.pages.inspectionDetail.inspection?.inspection,
    creator: state.pages.inspectionDetail.inspection?.creator,
    scoring: filterScoredHazards(state),
});
const mapDispatchToProps = (dispatch) => ({
    getInspectionByToken(token) {
        dispatch(inspectionDetailActions.getInspectionByExportToken(token));
    },
    reset() {
        dispatch(inspectionDetailActions.reset());
    },
});
export default connect(mapStateToProps, mapDispatchToProps)(InspectionExport);
